import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CtaLink } from "@/components/landing/CtaLink";
import { LandingNav } from "./LandingNav";

const fadeIn = {
  hidden: { opacity: 0, y: 16 } as const,
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.09 } },
};

const STEPS = [
  { n: "01", label: "Store your knowledge" },
  { n: "02", label: "Upload the tender" },
  { n: "03", label: "Review & submit" },
];

export function LandingHero() {
  return (
    <section className="relative flex flex-col">
      <LandingNav />

      <div className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-80px)] sm:min-h-screen px-5 sm:px-6 pt-16 sm:pt-0 pb-16 sm:pb-0">
        <div className="relative w-full max-w-[1040px] mx-auto text-center sm:-mt-10">
          {/* Ambient glow */}
          <div
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              left: "50%",
              top: "42%",
              transform: "translate(-50%, -50%)",
              width: "130%",
              height: "150%",
              background:
                "radial-gradient(ellipse 50% 45% at 50% 50%, hsl(var(--primary) / 0.12) 0%, hsl(var(--primary) / 0.035) 50%, transparent 100%)",
            }}
          />

          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="relative"
          >
            {/* Step markers */}
            <motion.div
              variants={fadeIn}
              className="mx-auto w-fit hidden sm:flex sm:flex-row sm:flex-wrap sm:items-center justify-center gap-6 md:gap-10 mb-11"
            >
              {STEPS.map((step, i) => (
                <div key={step.n} className="flex items-center gap-4 sm:gap-6 md:gap-10">
                  {i > 0 && (
                    <span
                      aria-hidden
                      className="hidden sm:block w-px h-7"
                      style={{
                        background:
                          "linear-gradient(to bottom, transparent, hsl(var(--foreground) / 0.14), transparent)",
                      }}
                    />
                  )}
                  <div className="flex items-center gap-3">
                    <span
                      className="flex items-center justify-center h-8 w-8 shrink-0 rounded-full text-[10px] font-bold tracking-[0.08em] text-primary"
                      style={{
                        background: "hsl(220 15% 7%)",
                        boxShadow: "inset 0 0 0 1px hsl(var(--primary) / 0.35)",
                      }}
                    >
                      {step.n}
                    </span>
                    <span className="text-[10.5px] sm:text-[10.5px] md:text-[11px] font-medium uppercase tracking-[0.16em] md:tracking-[0.2em] text-white/60 whitespace-nowrap">
                      {step.label}
                    </span>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={fadeIn}
              className="mx-auto max-w-[16ch] sm:max-w-[22ch] font-bold tracking-[-0.03em] leading-[1.1] text-white"
              style={{ fontSize: "clamp(1.75rem, 5.6vw, 4.25rem)" }}
            >
              Never start a bid from scratch.{" "}
              <span className="block text-white/50">
                Start from your best work.
              </span>
            </motion.h1>

            {/* Supporting sentence */}
            <motion.p
              variants={fadeIn}
              className="mt-6 sm:mt-8 mx-auto max-w-[520px] text-[14.5px] sm:text-[16.5px] md:text-[17.5px] !leading-[1.6]"
            >
              Upload a tender and Sorvaine drafts each section using your company
              knowledge.
            </motion.p>

            {/* CTAs */}
            <motion.div
              variants={fadeIn}
              className="mt-9 sm:mt-11 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-[320px] sm:max-w-none mx-auto"
            >
              <CtaLink to="/signup" cta="get_started" location="hero">
                <Button className="relative w-full sm:w-auto h-12 sm:h-[52px] rounded-xl px-8 bg-primary hover:bg-primary/90 text-[15px] sm:text-base !text-primary-foreground font-semibold tracking-[0.01em] transition-all duration-200 hover:-translate-y-[1px] shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.5)] hover:shadow-[0_14px_36px_-10px_hsl(var(--primary)/0.6)]">
                  Get started
                  <ArrowRight className="ml-2 w-4 h-4 sm:static absolute right-5" />
                </Button>
              </CtaLink>
              <CtaLink to="/book-demo" cta="book_demo" location="hero">
                <Button
                  variant="outline"
                  className="relative w-full sm:w-auto h-12 sm:h-[52px] rounded-xl px-8 border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 text-[15px] sm:text-base text-white font-semibold tracking-[0.01em] transition-all duration-200"
                >
                  Book a demo
                  <ArrowRight className="ml-2 w-4 h-4 sm:static absolute right-5" />
                </Button>
              </CtaLink>
            </motion.div>

            {/* Trust line */}
            <motion.div
              variants={fadeIn}
              className="mt-6 flex items-center justify-center gap-2"
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-primary/80 animate-pulse"
              />
              <span className="text-[10.5px] sm:text-xs uppercase tracking-[0.18em] text-white/45">
                No credit card required
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
